import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getCart as getCartAPI,
  addToCart as addToCartAPI,
  updateCartItem as updateCartItemAPI,
  removeFromCart as removeFromCartAPI,
  clearCart as clearCartAPI
} from '../services/cartService';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const guestCartKey = 'mmw_guest_cart';

  // ✅ Load cart on app start
  useEffect(() => {
    loadCart();
  }, [user]);

  const loadCart = async () => {
    if (!user) {
      setItems(JSON.parse(localStorage.getItem(guestCartKey) || '[]'));
      return;
    }
    try {
      const data = await getCartAPI();
      setItems(data.items || []);
    } catch (err) {
      console.error('Load cart failed', err);
      setItems([]); // ✅ fail-safe
    }
  };

  // ✅ ADD TO CART
  const addToCart = async (data) => {
    if (!user) {
      const product = data.product;
      if (!product) throw new Error('Product details are required');
      setItems((current) => {
        const index = current.findIndex((item) =>
          item.product._id === data.productId && item.size === data.size && item.color === data.color
        );
        const next = [...current];
        if (index >= 0) next[index] = { ...next[index], qty: next[index].qty + data.qty };
        else next.push({ product, qty: data.qty, size: data.size, color: data.color });
        localStorage.setItem(guestCartKey, JSON.stringify(next));
        return next;
      });
      return;
    }
    const res = await addToCartAPI(data);
    setItems(res.items || []);
  };

  // ✅ UPDATE QTY
  const updateCartItem = async (data) => {
    if (!user) {
      setItems((current) => {
        const next = current.map((item) =>
          item.product._id === data.productId && item.size === data.size && item.color === data.color
            ? { ...item, qty: data.qty }
            : item
        );
        localStorage.setItem(guestCartKey, JSON.stringify(next));
        return next;
      });
      return;
    }
    const res = await updateCartItemAPI(data);
    setItems(res.items || []);
  };

  // ✅ ✅ ✅ REMOVE FROM CART (THIS WAS MISSING)
  const removeFromCart = async (productId, size, color) => {
    if (!user) {
      setItems((current) => {
        const next = current.filter((item) =>
          !(item.product._id === productId && item.size === size && item.color === color)
        );
        localStorage.setItem(guestCartKey, JSON.stringify(next));
        return next;
      });
      return;
    }
    const res = await removeFromCartAPI(productId, size, color);
    setItems(res.items || []);
  };

  // ✅ CLEAR CART
  const clearCart = async () => {
    if (!user) {
      localStorage.removeItem(guestCartKey);
      setItems([]);
      return;
    }
    await clearCartAPI();
    setItems([]);
  };

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        updateCartItem,
        removeFromCart, // ✅ MUST BE EXPOSED HERE
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
